import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  warehousesApi, warehouseLocationsApi, itemBinsApi,
  type ItemDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ItemPicker } from "@/components/Pickers";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

export function ItemBinsPage() {
  const qc = useQueryClient();
  const [warehouseId, setWarehouseId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const canManage = useAuth(s => s.hasPermission("inventory.location.manage"));

  const warehouses = useQuery({ queryKey: ["warehouses"], queryFn: () => warehousesApi.list({ pageSize: 200 }), staleTime: 5 * 60_000 });

  const query = useQuery({
    queryKey: ["item-bins", warehouseId],
    queryFn: () => itemBinsApi.list({ warehouseId, pageSize: 500 }),
    enabled: !!warehouseId,
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Assignment removed" },
    mutationFn: (id: string) => itemBinsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["item-bins"] }),
  });
  const primaryMutation = useMutation({
    meta: { successMessage: "Primary bin updated" },
    mutationFn: ({ id, isPrimary }: { id: string; isPrimary: boolean }) => itemBinsApi.update(id, isPrimary),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["item-bins"] }),
  });

  const rows = query.data?.items ?? [];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Inventory", "Item bins"]}
        title="Item bin assignments"
        actions={
          canManage && warehouseId ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              Assign item to bin
            </Button>
          ) : undefined
        }
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField select label="Warehouse" size="small" value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)} sx={{ minWidth: 320 }}>
          <MenuItem value=""><em>— Pick a warehouse —</em></MenuItem>
          {warehouses.data?.items.map(w => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
        </TextField>
      </Paper>

      {!warehouseId && <Alert severity="info">Pick a warehouse to view and assign item bins. Define bins under Admin → Warehouse Locations first.</Alert>}
      {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}

      {warehouseId && (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Bin</TableCell>
                  <TableCell align="center">Primary</TableCell>
                  {canManage && <TableCell align="right" width={96}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell><b>{r.itemSku}</b> · {r.itemName}</TableCell>
                    <TableCell>
                      {r.locationCode} — {r.locationName}
                      <Chip size="small" sx={{ ml: 1 }} label={r.locationLevel} />
                    </TableCell>
                    <TableCell align="center">
                      {canManage ? (
                        <Tooltip title={r.isPrimary ? "Primary bin" : "Set as primary"}>
                          <IconButton size="small" color={r.isPrimary ? "warning" : "default"}
                            onClick={() => primaryMutation.mutate({ id: r.id, isPrimary: !r.isPrimary })}>
                            {r.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      ) : (r.isPrimary ? <StarIcon fontSize="small" color="warning" /> : "—")}
                    </TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Tooltip title="Remove"><IconButton size="small" color="error"
                          onClick={() => { if (confirm(`Remove ${r.itemName} from ${r.locationCode}?`)) deleteMutation.mutate(r.id); }}>
                          <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rows.length === 0 && !query.isLoading && (
                  <TableRow><TableCell colSpan={4}>
                    <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No item-bin assignments in this warehouse yet.
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {warehouseId && (
        <AssignDialog open={dialogOpen} onClose={() => setDialogOpen(false)} warehouseId={warehouseId} />
      )}
    </Box>
  );
}

function AssignDialog({ open, onClose, warehouseId }: { open: boolean; onClose: () => void; warehouseId: string }) {
  const qc = useQueryClient();
  const [item, setItem] = useState<ItemDto | null>(null);
  const [locationId, setLocationId] = useState("");
  const [isPrimary, setIsPrimary] = useState(false);

  useEffect(() => {
    if (open) { setItem(null); setLocationId(""); setIsPrimary(false); }
  }, [open]);

  // Only bins are assignable (not the intermediate zones/aisles/racks).
  const locations = useQuery({
    queryKey: ["warehouse-locations", warehouseId, "bins"],
    queryFn: () => warehouseLocationsApi.list({ warehouseId, pageSize: 500 }),
    enabled: open && !!warehouseId,
  });
  const bins = (locations.data?.items ?? []).filter(l => l.level === "Bin");

  const mutation = useMutation({
    meta: { successMessage: "Item assigned to bin" },
    mutationFn: () => itemBinsApi.create({ itemId: item!.id, warehouseId, warehouseLocationId: locationId, isPrimary }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["item-bins"] }); onClose(); },
  });

  const canSubmit = !!item && !!locationId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>Assign item to bin</ClosableDialogTitle>
      <DialogContent dividers>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}
        <Stack spacing={2}>
          <ItemPicker value={item} onChange={setItem} label="Item" required />
          <TextField select label="Bin *" size="small" fullWidth value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            helperText={bins.length === 0 ? "No bins defined — add Bin-level locations under Warehouse Locations" : undefined}>
            {bins.map(b => <MenuItem key={b.id} value={b.id}>{b.code} — {b.name}</MenuItem>)}
          </TextField>
          <FormControlLabel label="Primary bin for this item"
            control={<Switch checked={isPrimary} onChange={(_, v) => setIsPrimary(v)} />} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={() => mutation.mutate()}>
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}
